import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  getDefaultCycleLowSettings,
  getDefaultCycleHighSettings,
} from '@andywilliams/indicators';
import {
  getCycleLowFields,
  getCycleHighFields,
  buildSettingKey,
  LEGACY_WEEKLY_CYCLE_LOW_BASE_KEYS,
  LEGACY_WEEKLY_CYCLE_HIGH_BASE_KEYS,
} from '../../markets/cycleTuning/cycleTuningConfig';
import {
  updateSystemSetting,
  deleteSystemSetting,
} from '../../../api/admin';

const TIMEFRAME_LABELS = {
  weekly: 'Weekly',
  daily: 'Daily',
  hourly: 'Hourly',
};

const SIDE_LABELS = {
  low: { lower: 'low', title: 'Lows', sentence: 'cycle-low' },
  high: { lower: 'high', title: 'Highs', sentence: 'cycle-high' },
};

const toFormValue = (value, fallback) => {
  if (value === undefined || value === null || value === '') {
    return String(fallback ?? '');
  }
  return String(value);
};

/**
 * CycleSettingsSection — admin bulk-edit form for one (timeframe × side)
 * combination of cycle detection parameters.
 *
 * Six instances render under the Indicator Settings tab: low + high for
 * weekly, daily, and hourly. Each instance owns its own scope-symbol,
 * form state, validation, and save/reset handlers — they don't share
 * state across timeframes or sides because edits in one section
 * shouldn't bleed into another.
 *
 * Field metadata + key shapes come from cycleTuningConfig (which uses
 * the library's per-TF key helpers). Library defaults
 * (getDefaultCycleLowSettings / getDefaultCycleHighSettings) hydrate
 * the form when no admin override exists.
 *
 * On weekly only, the reset handler also fires legacy-schema deletes
 * (`indicators.weeklyCycleLow.*` / `indicators.weeklyCycleHigh.*`) so
 * the library's dual-read can't resurrect a pre-Phase-2b override.
 * Daily and hourly never had a legacy schema.
 */
const CycleSettingsSection = ({
  timeframe,
  side,
  settings,
  symbols,
  loadSettings,
  setSnackbar,
  setIndicatorError,
  indicatorBusy,
  settingsLoading,
}) => {
  const tfLabel = TIMEFRAME_LABELS[timeframe] || timeframe;
  const sideLabel = SIDE_LABELS[side];
  const isWeekly = timeframe === 'weekly';

  // Field defs + library defaults for this (TF, side) combo. Memoised so
  // they're stable across renders that don't change tf/side, which keeps
  // the hydration effect's deps clean.
  const fields = useMemo(
    () => (side === 'low' ? getCycleLowFields(timeframe) : getCycleHighFields(timeframe)),
    [timeframe, side],
  );
  const defaults = useMemo(
    () => (side === 'low'
      ? getDefaultCycleLowSettings(timeframe)
      : getDefaultCycleHighSettings(timeframe)),
    [timeframe, side],
  );
  const legacyBaseKeys = side === 'low'
    ? LEGACY_WEEKLY_CYCLE_LOW_BASE_KEYS
    : LEGACY_WEEKLY_CYCLE_HIGH_BASE_KEYS;

  const buildDefaultForm = useCallback(() => fields.reduce((acc, f) => {
    acc[f.field] = toFormValue(defaults[f.field], defaults[f.field]);
    return acc;
  }, {}), [fields, defaults]);

  const [scopeSymbol, setScopeSymbol] = useState('');
  const [form, setForm] = useState(buildDefaultForm);
  const [initialForm, setInitialForm] = useState(buildDefaultForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Hydrate the form from the settings dict whenever it changes or the
  // scope symbol changes. Picks per-symbol override → global → library
  // default, matching the library's runtime resolution order.
  useEffect(() => {
    if (!Array.isArray(settings) || !settings.length) {
      const fresh = buildDefaultForm();
      setForm(fresh);
      setInitialForm({ ...fresh });
      setFieldErrors({});
      return;
    }
    const symbolScope = scopeSymbol || null;
    const next = fields.reduce((acc, f) => {
      const fallback = defaults[f.field];
      const symbolKey = buildSettingKey(f.baseKey, symbolScope);
      const globalKey = buildSettingKey(f.baseKey, null);
      const symbolSetting = symbolScope && symbolKey
        ? settings.find((item) => item.key === symbolKey)
        : null;
      const globalSetting = globalKey
        ? settings.find((item) => item.key === globalKey)
        : null;
      const effective = symbolSetting?.value ?? globalSetting?.value ?? fallback;
      acc[f.field] = toFormValue(effective, fallback);
      return acc;
    }, {});
    setForm(next);
    setInitialForm({ ...next });
    setFieldErrors({});
  }, [settings, scopeSymbol, fields, defaults, buildDefaultForm]);

  const dirty = useMemo(
    () => fields.some((f) => form[f.field] !== initialForm[f.field]),
    [form, initialForm, fields],
  );

  const validate = useCallback((candidate) => {
    const errors = {};
    fields.forEach((f) => {
      const parsed = Number(candidate[f.field]);
      if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
        errors[f.field] = 'Must be a non-negative integer.';
      }
    });
    return errors;
  }, [fields]);

  const handleSave = async () => {
    const errors = validate(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setIndicatorError('Please fix the highlighted fields before saving.');
      return;
    }
    setSaving(true);
    setIndicatorError('');
    try {
      const symbolScope = scopeSymbol || null;
      await Promise.all(fields.map((f) => {
        const key = buildSettingKey(f.baseKey, symbolScope);
        if (!key) {return Promise.resolve();}
        return updateSystemSetting(key, Number(form[f.field]));
      }));
      setSnackbar({
        open: true,
        severity: 'success',
        message: symbolScope
          ? `${tfLabel} ${sideLabel.sentence} settings updated for ${symbolScope}.`
          : `${tfLabel} ${sideLabel.sentence} settings updated successfully.`,
      });
      await loadSettings();
    } catch (error) {
      const message = error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || `Failed to update ${tfLabel.toLowerCase()} ${sideLabel.sentence} settings.`;
      setIndicatorError(message);
      setSnackbar({ open: true, severity: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setIndicatorError('');
    setFieldErrors({});
    try {
      const symbolScope = scopeSymbol || null;
      // On weekly only: fire-and-forget legacy-schema deletes so the
      // library's dual-read can't resurrect a pre-Phase-2b override after
      // the new-schema key is cleared. Daily/hourly never had a legacy
      // schema so we skip this branch.
      if (isWeekly) {
        fields.forEach((f) => {
          const legacyKey = buildSettingKey(legacyBaseKeys[f.field], symbolScope);
          if (legacyKey) {
            deleteSystemSetting(legacyKey).catch(() => null);
          }
        });
      }
      await Promise.all(fields.map((f) => {
        const key = buildSettingKey(f.baseKey, symbolScope);
        if (!key) {return Promise.resolve();}
        return deleteSystemSetting(key).catch((error) => {
          if (error?.response?.status === 404) {return null;}
          throw error;
        });
      }));
      setSnackbar({
        open: true,
        severity: 'success',
        message: symbolScope
          ? `${tfLabel} ${sideLabel.sentence} settings for ${symbolScope} reset to global defaults.`
          : `${tfLabel} ${sideLabel.sentence} settings reset to defaults.`,
      });
      await loadSettings();
    } catch (error) {
      const message = error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || `Failed to reset ${tfLabel.toLowerCase()} ${sideLabel.sentence} settings.`;
      setIndicatorError(message);
      setSnackbar({ open: true, severity: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        {tfLabel} cycle-{sideLabel.lower} detection scores and confirms major{' '}
        {sideLabel.lower}s using DSS, the 10-SMA, and optional cross-timeframe
        signals. Tune these parameters to control clustering, confirmation
        windows, and the acceptance threshold used across DWLF tools.
      </Alert>
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          {tfLabel} Cycle {sideLabel.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These settings drive the {tfLabel.toLowerCase()} {sideLabel.sentence}
          {' '}detector shared between backend analytics and the charting layer.
        </Typography>
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}>
          <TextField
            select
            label="Scope"
            value={scopeSymbol || '__global'}
            onChange={(event) => {
              const { value } = event.target;
              setScopeSymbol(value === '__global' ? '' : value);
            }}
            size="small"
            sx={{ minWidth: 260 }}
            disabled={saving || indicatorBusy || settingsLoading}
          >
            <MenuItem value="__global">Global defaults (all symbols)</MenuItem>
            {symbols.map((item) => (
              <MenuItem key={item.symbol} value={item.symbol}>
                {item.symbol}
                {item.name ? ` — ${item.name}` : ''}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary">
            {scopeSymbol
              ? `Editing overrides for ${scopeSymbol}.`
              : 'Editing global defaults applied when no symbol override exists.'}
          </Typography>
        </Box>
        <Box sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
        }}>
          {fields.map(({ field, label, helperText, inputProps }) => (
            <TextField
              key={field}
              label={label}
              value={form[field] ?? ''}
              onChange={(event) => setForm((prev) => ({
                ...prev,
                [field]: event.target.value,
              }))}
              disabled={saving || indicatorBusy || settingsLoading}
              type="number"
              inputProps={inputProps}
              error={Boolean(fieldErrors[field])}
              helperText={fieldErrors[field] || helperText}
              fullWidth
            />
          ))}
        </Box>
        <Stack
          spacing={0.75}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          sx={{ mt: 1 }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: 0.5 }}
          >
            {tfLabel} Cycle-{sideLabel.title === 'Lows' ? 'Low' : 'High'} Actions
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="flex-end">
            <Button
              variant="text"
              color="inherit"
              onClick={() => {
                setForm({ ...initialForm });
                setFieldErrors({});
              }}
              disabled={!dirty || saving || indicatorBusy || settingsLoading}
            >
              Revert Changes
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              disabled={saving || indicatorBusy || settingsLoading}
            >
              Reset to Defaults
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!dirty || saving || indicatorBusy || settingsLoading}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Divider />
    </Stack>
  );
};

export default CycleSettingsSection;
